/* eslint-disable max-lines */
/* eslint-disable max-depth */
/* eslint-disable max-statements */
/* eslint-disable complexity */
import { useCallback, useContext } from 'react';
import type { DSComboboxT } from '../../react-desc-prop-types.js';
import { ComboBoxContext } from '../../ComboBoxCTX.js';
import {
  findInCircularList,
  getOptions,
  getSuggestedValueOnChange,
  getLastValueSelected,
} from '../../utils/listHelper.js';
import { INTERNAL_MENU_OPTION_TYPES, MENU_OPTION_TYPES, MENU_CONTROL_REASONS } from '../../constants.js';
const isOptionFocuseable = (opt: DSComboboxT.OptionTypes): boolean => !['section', 'separator'].includes(opt.type);

const optionHasFirstLetter = (opt: DSComboboxT.OptionTypes, letter: string): boolean =>
  opt.label !== undefined && opt.label.toLowerCase().startsWith(letter.toLowerCase());
export const useKeyboardNavigation = () => {
  const {
    props: {
      allOptions,
      isNonClearable,
      onCancel,
      disabled,
      onKeyDown,
      onChange,
      onCreate,
      onFilter,
      onSelectAll,
      inline,
      selectedValues,
      withoutPortal,
      onlySelectable,
      applyAriaDisabled,
      readOnly,
      isMenuOpenableWithReadOnly,
    },
    inputValue,
    correctOptions: filteredOptions,
    menuState,
    focusOptionIdx,
    listRef,
    setMenuState,
    setInputValue,
    scrollOptionIntoView,
    setFocusOptionIdx,
  } = useContext(ComboBoxContext);

  const multiple = Array.isArray(selectedValues);
  const selectableOptions = getOptions(filteredOptions);
  const currentItemIndex = filteredOptions.findIndex((opt) => opt.dsId === focusOptionIdx);
  const currentItem = filteredOptions.find((item) => item.dsId === focusOptionIdx);

  const onInputKeyDown: React.KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (applyAriaDisabled || (readOnly && !isMenuOpenableWithReadOnly) || disabled) return;

      // =============================================================================
      // CUSTOM KEYS
      // =============================================================================
      if (onKeyDown && currentItem?.type === MENU_OPTION_TYPES.OPTION) onKeyDown(e, currentItem);

      if (['ArrowDown', 'ArrowUp', 'Enter', 'Spacebar'].includes(e.key) && !menuState) {
        setMenuState(true, MENU_CONTROL_REASONS.OPEN, e);
      }
      // =============================================================================
      // ESCAPE
      // =============================================================================
      if (e.key === 'Escape') {
        if (onCancel) onCancel();
        if (inputValue) {
          if (onFilter) onFilter(allOptions, inputValue);
          setInputValue('');
        }
        if (!inline) setMenuState(false, MENU_CONTROL_REASONS.CLOSE, e);
      }
      // =============================================================================
      // ENTER KEY TO CREATE ELEMENTS
      // =============================================================================
      if (!readOnly && e.key === 'Enter' && currentItem?.type === INTERNAL_MENU_OPTION_TYPES.CREATABLE && onCreate) {
        onCreate(inputValue);
        // blank active item to force search last one
        setFocusOptionIdx('');
        if (onFilter) onFilter(allOptions, '');
        setInputValue('');
        return;
      }

      // the following code is wrong but kept for backward compatibility to avoid breaking changes
      // when readonly the idea is not to open the menu when typing  but currently it opens the menu
      // =============================================================================
      // OPEN MENU ON TYPE WHEN READONLY AND ISMENUOPENABLEWITHREADONLY IS TRUE
      // =============================================================================
      if (isMenuOpenableWithReadOnly && e.key.length === 1 && !e.ctrlKey && !e.altKey && !e.metaKey) {
        if (!menuState) {
          setMenuState(true, MENU_CONTROL_REASONS.OPEN, e);
        }
      }

      // =============================================================================
      // Enter, space and Tab on selection
      // =============================================================================
      if (e.key === 'Enter' || (e.key === ' ' && e.altKey) || (e.key === 'Tab' && !multiple && !inline)) {
        if (readOnly || currentItem?.applyAriaDisabled) return;

        if (e.key !== 'Tab') e.preventDefault();
        e.stopPropagation();

        if (
          focusOptionIdx !== '' &&
          (menuState || inline) &&
          currentItem?.type === MENU_OPTION_TYPES.OPTION &&
          !currentItem.disabled
        ) {
          if (onFilter) onFilter(allOptions, '');
          setInputValue('');
          if (!multiple) {
            setMenuState(false, MENU_CONTROL_REASONS.SELECT_OPTION, e);
          }
          onChange(getSuggestedValueOnChange(currentItem, selectedValues, isNonClearable), currentItem, e);
        }
      }

      if (e.key === 'Enter' && e.altKey) {
        setMenuState(false, 'selectOption', e);
      }

      if (e.key === 'a' && e.ctrlKey && multiple && onSelectAll) {
        onSelectAll(
          filteredOptions.filter((option) => option.type === 'option' && !option.disabled),
          e,
        );
      }
      // =============================================================================
      // ARROWS UP AND DOWN: LOGIC TO CALCULATE NEXT OR PREV ITEM TO PSEUDOFOCUS FROM INPUT
      // =============================================================================

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        e.stopPropagation();
        if ((menuState || inline) && (selectableOptions.length || onCreate)) {
          const nextItemIndexIndex = findInCircularList(filteredOptions, currentItemIndex, isOptionFocuseable);
          if (nextItemIndexIndex > -1) {
            setFocusOptionIdx(filteredOptions[nextItemIndexIndex].dsId);
            scrollOptionIntoView(filteredOptions[nextItemIndexIndex].dsId);
          }
        }
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        e.stopPropagation();
        if (!selectableOptions.length && !onCreate) return;
        const prevItemIndex = findInCircularList(
          filteredOptions,
          currentItemIndex === -1 ? 0 : currentItemIndex,
          isOptionFocuseable,
          -1,
        );
        if (prevItemIndex > -1) {
          if (menuState || inline) {
            setFocusOptionIdx(filteredOptions[prevItemIndex].dsId);
            scrollOptionIntoView(filteredOptions[prevItemIndex].dsId);
          } else {
            // open menu and search last item to focused
            setMenuState(true, MENU_CONTROL_REASONS.OPEN, e);
            const lastItemIndex = findInCircularList(filteredOptions, 0, isOptionFocuseable, -1);
            setFocusOptionIdx(filteredOptions[lastItemIndex].dsId);
            setTimeout(() => {
              scrollOptionIntoView(filteredOptions[lastItemIndex].dsId);
            });
          }
        }
      }

      // =============================================================================
      // BACKSPACE
      // =============================================================================
      if (e.key === 'Backspace' && e.currentTarget.value.length <= 0 && !inline) {
        if (readOnly) return;
        const lastValue = getLastValueSelected(selectedValues);
        if (!e.currentTarget.value && lastValue && !isNonClearable && !lastValue.applyAriaDisabled) {
          onChange(getSuggestedValueOnChange(lastValue, selectedValues), lastValue, e);
        }
      }

      if (e.key === 'Tab' && !inline && menuState) {
        setMenuState(false, MENU_CONTROL_REASONS.BLUR, e);
      }

      // =============================================================================
      // ONLYSELECTABLE LETTER/NUMBER KEYS TO FOCUS OPTIONS
      // =============================================================================

      if (onlySelectable && /^[a-zA-Z\d]$/.test(e.key)) {
        if (!menuState) {
          setMenuState(true, MENU_CONTROL_REASONS.OPEN, e);
        }

        const isOptionFocusableByLetter = (opt: DSComboboxT.OptionTypes): boolean =>
          isOptionFocuseable(opt) && optionHasFirstLetter(opt, e.key);

        const nextItemIndexIndex = findInCircularList(filteredOptions, currentItemIndex, isOptionFocusableByLetter);
        const nextItem = filteredOptions[nextItemIndexIndex];
        if (!nextItem) return;
        setFocusOptionIdx(filteredOptions[nextItemIndexIndex].dsId);
        scrollOptionIntoView(filteredOptions[nextItemIndexIndex].dsId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      onKeyDown,
      currentItem,
      onCreate,
      multiple,
      inputValue,
      inline,
      withoutPortal,
      setMenuState,
      onCancel,
      onFilter,
      setInputValue,
      listRef,
      focusOptionIdx,
      menuState,
      selectableOptions.length,
      filteredOptions,
      currentItemIndex,
      setFocusOptionIdx,
      selectedValues,
      onChange,
      isNonClearable,
    ],
  );

  return { onInputKeyDown };
};
